import { Box, BoxProps } from '@nimbus-ds/components';
import { useTranslation } from 'react-i18next';
import { Stack, useResponsive } from 'commons/components';
import { formatCurrency } from 'commons/utils';
import { useGetCurrency } from 'domains/Auth/hooks';
import { useTranslationDashboard } from 'domains/Dashboard/hooks';
import { thousandSeparator } from 'domains/Statistics/utils';
import Skeleton from './Skeleton';
import StatsDailyValueCard from './StatsDailyValueCard';

export const STYLE_STATS_DAILY_TAB_CONTENT: BoxProps = {
  display: 'grid',
  gridGap: 'none',
};

interface StatsDailyTabContentValues {
  visits: number;
  salesQty: number;
  billing?: number;
  averageTicket?: number;
}

interface StatsDailyTabContentProps {
  values?: StatsDailyTabContentValues;
  hideAmounts: boolean;
  showInline?: boolean;
}

function StatsDailyTabContent({
  values,
  hideAmounts,
  showInline,
}: StatsDailyTabContentProps) {
  const {
    i18n: { language },
  } = useTranslation();
  const { isMobile } = useResponsive();
  const t = useTranslationDashboard();
  const { code } = useGetCurrency();

  const shouldShowInlineGrid = showInline && !isMobile;

  const getFormatedValue = (value?: number, isCurrencyFormat?: boolean) => {
    if (hideAmounts && isCurrencyFormat) return '*****';
    // returning a hyphen helps us to identify when an error occurs in the call to the service
    if (value === undefined) return '-';
    if (isCurrencyFormat) {
      return formatCurrency(value, code, language);
    }

    return thousandSeparator(value, true);
  };

  return (
    <Stack column align="stretch">
      <Box
        {...STYLE_STATS_DAILY_TAB_CONTENT}
        gridTemplateColumns={
          shouldShowInlineGrid ? 'repeat(4, 1fr)' : 'repeat(2, 1fr)'
        }
      >
        <StatsDailyValueCard
          title={t('tabs.visits')}
          value={getFormatedValue(values?.visits)}
          textTooltip={t('tabs.visitsTooltip')}
          border={shouldShowInlineGrid ? 'left' : 'top-left'}
        />
        <StatsDailyValueCard
          title={t('tabs.sales')}
          value={getFormatedValue(values?.salesQty)}
          border={shouldShowInlineGrid ? 'none' : 'top-right'}
        />
        <StatsDailyValueCard
          title={t('tabs.billing')}
          value={getFormatedValue(values?.billing, true)}
          textTooltip={t('tabs.billingTooltip')}
          border={shouldShowInlineGrid ? 'none' : 'bottom-left'}
        />
        <StatsDailyValueCard
          title={t('tabs.averageTicket')}
          value={getFormatedValue(values?.averageTicket, true)}
          textTooltip={t('tabs.averageTicketTooltip')}
          border={shouldShowInlineGrid ? 'right' : 'bottom-right'}
        />
      </Box>
    </Stack>
  );
}

StatsDailyTabContent.Skeleton = Skeleton;

export default StatsDailyTabContent;
